/**
 * Permission Cache Manager (Shared)
 *
 * Platform-agnostic permission caching.
 * Uses the storage adapter for persistence.
 */

import { storage } from '../adapters/storage';

class PermissionCacheManager {
  constructor() {
    this.CACHE_KEY = 'crm_user_permissions';
    this.CACHE_VERSION_KEY = 'crm_permissions_version';
    this.EXPIRY_KEY = 'crm_permissions_expiry';
    this.MAX_CACHE_AGE = 5 * 60 * 1000; // 5 minutes
    this.VERSION = '1.0.0';

    // In-memory cache for ultra-fast access
    this.memoryCache = {
      data: null,
      expiry: null,
      version: null,
    };
  }

  /**
   * Check if cached permissions are valid and not expired
   * Checks in-memory cache first (fastest), then falls back to storage
   */
  isCacheValid() {
    try {
      // First check in-memory cache (no I/O overhead)
      if (
        this.memoryCache.data &&
        this.memoryCache.expiry &&
        this.memoryCache.version === this.VERSION &&
        Date.now() <= this.memoryCache.expiry
      ) {
        return true;
      }

      // Fall back to storage (synchronous for web, may need async on mobile)
      const cachedData = storage.get(this.CACHE_KEY);
      const cachedVersion = storage.get(this.CACHE_VERSION_KEY);
      const expiry = storage.get(this.EXPIRY_KEY);

      if (!cachedData || !expiry) return false;

      const isExpired = Date.now() > parseInt(expiry);
      const isCorrectVersion = cachedVersion === this.VERSION;

      if (!isExpired && isCorrectVersion) {
        // Populate memory cache from storage for next access
        this.memoryCache.data = JSON.parse(cachedData);
        this.memoryCache.expiry = parseInt(expiry);
        this.memoryCache.version = cachedVersion;
        return true;
      }

      return false;
    } catch (error) {
      console.warn('Permission cache validation failed:', error);
      return false;
    }
  }

  /**
   * Async version of isCacheValid for mobile compatibility
   */
  async isCacheValidAsync() {
    try {
      // First check in-memory cache
      if (
        this.memoryCache.data &&
        this.memoryCache.expiry &&
        this.memoryCache.version === this.VERSION &&
        Date.now() <= this.memoryCache.expiry
      ) {
        return true;
      }

      // Fall back to async storage
      const [cachedData, cachedVersion, expiry] = await Promise.all([
        storage.getAsync(this.CACHE_KEY),
        storage.getAsync(this.CACHE_VERSION_KEY),
        storage.getAsync(this.EXPIRY_KEY),
      ]);

      if (!cachedData || !expiry) return false;

      const isExpired = Date.now() > parseInt(expiry);
      const isCorrectVersion = cachedVersion === this.VERSION;

      if (!isExpired && isCorrectVersion) {
        this.memoryCache.data = JSON.parse(cachedData);
        this.memoryCache.expiry = parseInt(expiry);
        this.memoryCache.version = cachedVersion;
        return true;
      }

      return false;
    } catch (error) {
      console.warn('Permission cache validation failed:', error);
      return false;
    }
  }

  /**
   * Get cached permissions (synchronous)
   */
  getCachedPermissions() {
    try {
      // First try in-memory cache
      if (
        this.memoryCache.data &&
        this.memoryCache.expiry &&
        Date.now() <= this.memoryCache.expiry
      ) {
        return this.memoryCache.data;
      }

      // Fall back to storage
      const cached = storage.get(this.CACHE_KEY);
      const expiry = storage.get(this.EXPIRY_KEY);

      if (cached && expiry && Date.now() <= parseInt(expiry)) {
        const data = JSON.parse(cached);
        // Populate memory cache for next access
        this.memoryCache.data = data;
        this.memoryCache.expiry = parseInt(expiry);
        this.memoryCache.version = this.VERSION;
        return data;
      }

      return null;
    } catch (error) {
      console.warn('Failed to get cached permissions:', error);
      return null;
    }
  }

  /**
   * Async version of getCachedPermissions for mobile compatibility
   */
  async getCachedPermissionsAsync() {
    try {
      // First try in-memory cache
      if (
        this.memoryCache.data &&
        this.memoryCache.expiry &&
        Date.now() <= this.memoryCache.expiry
      ) {
        return this.memoryCache.data;
      }

      // Fall back to async storage
      const [cached, expiry] = await Promise.all([
        storage.getAsync(this.CACHE_KEY),
        storage.getAsync(this.EXPIRY_KEY),
      ]);

      if (cached && expiry && Date.now() <= parseInt(expiry)) {
        const data = JSON.parse(cached);
        this.memoryCache.data = data;
        this.memoryCache.expiry = parseInt(expiry);
        this.memoryCache.version = this.VERSION;
        return data;
      }

      return null;
    } catch (error) {
      console.warn('Failed to get cached permissions:', error);
      return null;
    }
  }

  /**
   * Cache permissions with expiry (synchronous)
   */
  setCachedPermissions(permissions, companyId, userId, roleType, isOwner) {
    try {
      const cacheData = {
        permissions,
        companyId,
        userId,
        roleType,
        isOwner,
        cachedAt: Date.now(),
        version: this.VERSION,
      };

      const expiry = Date.now() + this.MAX_CACHE_AGE;

      // Update in-memory cache first
      this.memoryCache.data = cacheData;
      this.memoryCache.expiry = expiry;
      this.memoryCache.version = this.VERSION;

      // Also update storage for persistence
      storage.set(this.CACHE_KEY, JSON.stringify(cacheData));
      storage.set(this.CACHE_VERSION_KEY, this.VERSION);
      storage.set(this.EXPIRY_KEY, expiry.toString());

      console.log('[PermissionCache] Permissions cached for 5 minutes');
      return true;
    } catch (error) {
      console.warn('Failed to cache permissions:', error);
      return false;
    }
  }

  /**
   * Async version for mobile compatibility
   */
  async setCachedPermissionsAsync(permissions, companyId, userId, roleType, isOwner) {
    try {
      const cacheData = {
        permissions,
        companyId,
        userId,
        roleType,
        isOwner,
        cachedAt: Date.now(),
        version: this.VERSION,
      };

      const expiry = Date.now() + this.MAX_CACHE_AGE;

      // Update in-memory cache first
      this.memoryCache.data = cacheData;
      this.memoryCache.expiry = expiry;
      this.memoryCache.version = this.VERSION;

      // Also update async storage
      await Promise.all([
        storage.setAsync(this.CACHE_KEY, JSON.stringify(cacheData)),
        storage.setAsync(this.CACHE_VERSION_KEY, this.VERSION),
        storage.setAsync(this.EXPIRY_KEY, expiry.toString()),
      ]);

      console.log('[PermissionCache] Permissions cached for 5 minutes');
      return true;
    } catch (error) {
      console.warn('Failed to cache permissions:', error);
      return false;
    }
  }

  /**
   * Clear permission cache (synchronous)
   */
  clearCache() {
    try {
      // Clear in-memory cache
      this.memoryCache.data = null;
      this.memoryCache.expiry = null;
      this.memoryCache.version = null;

      // Clear storage
      storage.remove(this.CACHE_KEY);
      storage.remove(this.CACHE_VERSION_KEY);
      storage.remove(this.EXPIRY_KEY);

      console.log('[PermissionCache] Cache cleared');
      return true;
    } catch (error) {
      console.warn('Failed to clear permission cache:', error);
      return false;
    }
  }

  /**
   * Async version for mobile compatibility
   */
  async clearCacheAsync() {
    try {
      // Clear in-memory cache
      this.memoryCache.data = null;
      this.memoryCache.expiry = null;
      this.memoryCache.version = null;

      // Clear async storage
      await Promise.all([
        storage.removeAsync(this.CACHE_KEY),
        storage.removeAsync(this.CACHE_VERSION_KEY),
        storage.removeAsync(this.EXPIRY_KEY),
      ]);

      console.log('[PermissionCache] Cache cleared');
      return true;
    } catch (error) {
      console.warn('Failed to clear permission cache:', error);
      return false;
    }
  }

  /**
   * Get cache age in milliseconds
   */
  getCacheAge() {
    try {
      // Try memory cache first
      if (this.memoryCache.data?.cachedAt) {
        return Date.now() - this.memoryCache.data.cachedAt;
      }

      const cachedData = storage.get(this.CACHE_KEY);
      if (!cachedData) return null;

      const parsed = JSON.parse(cachedData);
      return Date.now() - parsed.cachedAt;
    } catch (error) {
      return null;
    }
  }
}

export const permissionCache = new PermissionCacheManager();

// Utility function for clearing cache on permission updates
export const invalidatePermissionCache = () => {
  permissionCache.clearCache();
};

// Auto-clear cache on successful permission updates
export const clearCacheAfterPermissionUpdate = async (updateFunction) => {
  try {
    const result = await updateFunction();
    if (result?.error) return result;

    // Clear cache to force refresh
    permissionCache.clearCache();

    console.log('[PermissionCache] Permissions updated - cache cleared');

    return result;
  } catch (error) {
    console.error('Permission update failed:', error);
    throw error;
  }
};
